// Nappiness wishlist: product handles saved in localStorage, no app required.
const STORAGE_KEY = 'nappiness-wishlist';
const CHANGE_EVENT = 'wishlist:change';

const readWishlist = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value : [];
  } catch {
    return [];
  }
};

const writeWishlist = (handles) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch {
    // Storage blocked (private mode); the heart still toggles for this page view.
  }
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { handles } }));
};

const toggleHandle = (handle) => {
  const handles = readWishlist();
  const next = handles.includes(handle) ? handles.filter((h) => h !== handle) : [handle, ...handles];
  writeWishlist(next);
  return next.includes(handle);
};

class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.productHandle;
    this.toggle = this.querySelector('button');
    if (!this.handle || !this.toggle) return;

    this.onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      const saved = toggleHandle(this.handle);
      this.render(saved);
      this.toggle.classList.add('is-bumping');
      setTimeout(() => this.toggle.classList.remove('is-bumping'), 200);
    };
    this.onChange = () => this.render(readWishlist().includes(this.handle));

    this.toggle.addEventListener('click', this.onClick);
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.onChange();
  }

  disconnectedCallback() {
    this.toggle?.removeEventListener('click', this.onClick);
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  render(saved) {
    this.toggle.setAttribute('aria-pressed', String(saved));
    this.toggle.setAttribute('aria-label', saved ? this.toggle.dataset.labelRemove : this.toggle.dataset.labelAdd);
  }
}

class WishlistCount extends HTMLElement {
  connectedCallback() {
    this.onChange = () => {
      const count = readWishlist().length;
      this.textContent = count ? String(count) : '';
      this.hidden = count === 0;
    };
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.onChange();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }
}

class WishlistGrid extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector('[data-wishlist-list]');
    this.empty = this.querySelector('[data-wishlist-empty]');
    this.onChange = () => this.render();
    document.addEventListener(CHANGE_EVENT, this.onChange);
    this.render();
  }

  disconnectedCallback() {
    document.removeEventListener(CHANGE_EVENT, this.onChange);
  }

  formatPrice(cents) {
    const currency = window.Shopify?.currency?.active || 'CZK';
    return new Intl.NumberFormat(document.documentElement.lang || 'cs-CZ', { style: 'currency', currency }).format(cents / 100);
  }

  async render() {
    const handles = readWishlist();
    this.empty.hidden = handles.length > 0;
    if (!handles.length) {
      this.list.replaceChildren();
      return;
    }

    const root = window.Shopify?.routes?.root || '/';
    const products = await Promise.all(
      handles.map((handle) =>
        fetch(`${root}products/${encodeURIComponent(handle)}.js`)
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    );

    const items = products.filter(Boolean).map((product) => {
      const item = document.createElement('li');
      item.className = 'wishlist-grid__item';

      const link = document.createElement('a');
      link.href = product.url;
      link.className = 'wishlist-grid__link';

      const media = document.createElement('div');
      media.className = 'wishlist-grid__media';
      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = `${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=720`;
        image.alt = product.title;
        image.loading = 'lazy';
        media.append(image);
      }

      const title = document.createElement('span');
      title.className = 'wishlist-grid__title';
      title.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'wishlist-grid__price';
      price.textContent = this.formatPrice(product.price);

      link.append(media, title, price);

      const heart = document.createElement('wishlist-button');
      heart.className = 'wishlist-button';
      heart.dataset.productHandle = product.handle;
      heart.innerHTML = document.querySelector('[data-wishlist-button-template]')?.innerHTML || '';
      heart.querySelector('button')?.setAttribute('data-label-add', `Přidat ${product.title} do oblíbených`);
      heart.querySelector('button')?.setAttribute('data-label-remove', `Odebrat ${product.title} z oblíbených`);

      item.append(link, heart);
      return item;
    });

    this.list.replaceChildren(...items);
    this.empty.hidden = items.length > 0;
  }
}

if (!customElements.get('wishlist-button')) customElements.define('wishlist-button', WishlistButton);
if (!customElements.get('wishlist-count')) customElements.define('wishlist-count', WishlistCount);
if (!customElements.get('wishlist-grid')) customElements.define('wishlist-grid', WishlistGrid);
